import { ClockIcon } from '@heroicons/react/24/outline';
import { type WorkQueueCase } from '../../../../services/workQueue';
import { expireDesign } from '../../../../services/approval';
import { ConfirmModalShell } from './ConfirmModalShell';
import { ConfirmModalHeader } from './ConfirmModalHeader';
import { ConfirmModalBody } from './ConfirmModalBody';
import { ConfirmModalFooter } from './ConfirmModalFooter';
import { PatientSummaryCard } from './PatientSummaryCard';
import { ReasonField } from './ReasonField';
import { REASON_MAX_LENGTH, REASON_MIN_LENGTH } from './reasonLimits';
import { useReasonActionSubmit } from './useReasonActionSubmit';

interface ExpireCaseModalProps {
    workCase: WorkQueueCase;
    onClose: () => void;
    onSuccess: (actionId: string) => void;
}

function ExpireConsequenceNotice() {
    return (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            This puts the case as &quot;Patient design expired&quot;. It can be reactivated later by
            an ODO admin or by the practice, which puts the case in Redesign.
        </p>
    );
}

export function ExpireCaseModal({ workCase: c, onClose, onSuccess }: ExpireCaseModalProps) {
    const { reason, setReason, submitting, canSubmit, confirm } = useReasonActionSubmit(
        c,
        onSuccess,
        {
            run: expireDesign,
            successToast: `Case expired: ${c.name}`,
            fallbackMessage: 'Failed to expire case. Please try again.',
            source: 'ExpireCaseModal.handleConfirm',
            action: 'expire-stale-design'
        }
    );

    return (
        <ConfirmModalShell widthClassName="w-[460px]" dismissible={!submitting} onDismiss={onClose}>
            <ConfirmModalHeader
                icon={ClockIcon}
                iconClassName="w-5 h-5 text-amber-500"
                title="Expire case"
                onClose={onClose}
                disabled={submitting}
            />

            <ConfirmModalBody className="space-y-3">
                <PatientSummaryCard name={c.name} practice={c.practice} />
                <ExpireConsequenceNotice />
                <ReasonField
                    value={reason}
                    onChange={setReason}
                    disabled={submitting}
                    label="Reason for expiring"
                    placeholder="e.g. Design is stale and the practice has not responded."
                    minLength={REASON_MIN_LENGTH}
                    maxLength={REASON_MAX_LENGTH}
                />
            </ConfirmModalBody>

            <ConfirmModalFooter
                onCancel={onClose}
                onConfirm={confirm}
                cancelDisabled={submitting}
                confirmDisabled={!canSubmit}
                confirmLabel={submitting ? 'Expiring…' : 'Confirm expire'}
                confirmColorClassName="bg-amber-600 text-white hover:bg-amber-700"
            />
        </ConfirmModalShell>
    );
}
